/*
  The home Population Report card's two small decisions, kept pure so they are unit-tested
  (component tests cannot run in this repo: React 17 against @testing-library/react 13).

  The card's figures themselves still come from `statsBandState`, which owns the payload
  guard, the loading/error rule and the labels. This file adds what the card shows on top:
  each grade's share of all cards graded, and the "Just certified" ticker's rows.

  Both mirror the events storefront's live stats panel (pcg-new-theme
  snippets/pcg-live-stats.liquid), so the two surfaces agree on what "share" and "just
  certified" mean.
*/

import { isMintGameCode } from "./categories";

/*
  A grade's share of all cards graded, as a percentage clamped to 0..100. A zero or bad
  total is no share at all rather than NaN or Infinity, which would otherwise reach a bar's
  width and a "NaN%" label.
*/
export const gradeShare = (value: number, total: number): number => {
  if (!isFinite(value) || !isFinite(total) || total <= 0 || value <= 0) {
    return 0;
  }
  return Math.min(100, (value / total) * 100);
};

/* One decimal, like the storefront: 0.7%, 31.5%. */
export const formatShare = (share: number): string => `${share.toFixed(1)}%`;

export interface JustCertified {
  gradedId: string;
  /* "Pristine 10", "NM-Mint 8" — the grade name and its number, trailing .0 dropped. */
  gradeLabel: string;
  cardName: string;
  /* "Duel Masters". Empty when the feed has none. */
  gameName: string;
  /* "Standard Slab". Empty when the feed has none. */
  slab: string;
  /* The completion date in the visitor's own time, "20 Oct 2026". Empty when absent or bad. */
  certifiedOn: string;
}

/* completedOn is UTC ("2026-10-20T03:28:17.000Z"); shown as a date in the visitor's own time. */
export const formatCertifiedOn = (iso: unknown): string => {
  if (typeof iso !== "string" || !iso) {
    return "";
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/*
  The "Graded in October" figure: /counts `monthGraded`, or null when the payload has no
  usable number (an older backend, or a bad value), so the figure hides instead of reading 0.
*/
export const selectMonthGraded = (counts: unknown): number | null => {
  if (typeof counts !== "object" || counts === null) {
    return null;
  }
  const value = (counts as { monthGraded?: unknown }).monthGraded;
  return typeof value === "number" && isFinite(value) && value >= 0 ? value : null;
};

/*
  "Graded in October". monthGraded resets at 00:00 UTC on the 1st, so this names the UTC
  month, not the visitor's: in New Zealand on the morning of 1 November it still says October.
*/
export const monthGradedLabel = (now: Date): string =>
  `Graded in ${now.toLocaleString("en-US", { month: "long", timeZone: "UTC" })}`;

/* Just enough of a v1 /gradings row. The feed has no contract, so every field is optional. */
interface RecentGradingRow {
  gradedId?: unknown;
  completed?: unknown;
  grade?: unknown;
  finalGrade?: unknown;
  cardName?: unknown;
  name?: unknown;
  game?: unknown;
  gameName?: unknown;
  slab?: unknown;
  completedOn?: unknown;
  cardInfo?: { gameCode?: unknown } | null;
}

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

/*
  The ticker's rows from GET /public/api/v1/gradings?limit=8&sort=-completedOn.

  Sorted by completion the feed is almost all certified cards, but a reopened card can still
  slip in: only completed rows with a grade are certified, which is the storefront's own
  filter. A row needs a cert number too, because the ticker links to its report.

  The grading office is never carried, even when an older backend still sends `office`: no
  public page may say which office graded a card (Robert, 2026-10-26).

  New Zealand Mint is barred from every public surface (OVD16), so its cards never show
  here either, whichever of the two game fields carries its code.
*/
export const selectJustCertified = (rows: unknown): JustCertified[] => {
  if (!Array.isArray(rows)) {
    return [];
  }

  const result: JustCertified[] = [];
  for (const raw of rows) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const row = raw as RecentGradingRow;
    const gradedId = text(row.gradedId);
    const grade = text(row.grade);
    if (row.completed !== true || !grade || !gradedId) {
      continue;
    }
    if (isMintGameCode(text(row.cardInfo?.gameCode)) || isMintGameCode(text(row.game))) {
      continue;
    }
    /*
      An authentication-only card comes back as grade "Authentic" with finalGrade 0, which
      would read "Authentic 0": a number only shows when there is a real grade.
    */
    const number =
      typeof row.finalGrade === "number" && isFinite(row.finalGrade) && row.finalGrade > 0
        ? ` ${String(row.finalGrade).replace(/\.0$/, "")}`
        : "";
    result.push({
      gradedId,
      gradeLabel: `${grade}${number}`,
      cardName: text(row.cardName) || text(row.name),
      gameName: text(row.gameName),
      slab: text(row.slab),
      certifiedOn: formatCertifiedOn(row.completedOn),
    });
  }
  return result;
};
